import React, { useState, useRef, useEffect } from "react";
import { v4 as uuid } from 'uuid';
import useMeasure from 'react-use-measure'
import { useSpring, animated } from '@react-spring/web'
import { DragDropContext, Draggable, Droppable, DroppableProps, DropResult } from 'react-beautiful-dnd';
import { Input } from '@/components/ui/input';
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '../tooltip/Tooltip';

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUploadImage } from '@src/components/node-info/hooks/useUploadImage.hook';
import { ImageEditModal } from './ImageEditModal';

export interface Option {
  value: string
  label: string
  userUploadId?: string
  cropMeta?: {width: number, height: number, x: number, y: number, unit: string}
}


interface InteractiveBadgeProps {
  option: Option
  isHovered: boolean
  onRemoveItem: () => void
  onBlur: (value: Option) => void
  disabled?: boolean
  withImageSelection?: boolean
}
const InteractiveBadge = ({ option, isHovered, onRemoveItem, onBlur, disabled }: InteractiveBadgeProps) => {
  const [isSelected, setIsSelected] = useState(false);
  const [currentValue, setCurrentValue] = useState(option.label)
  const inputRef = useRef<HTMLInputElement>(null);
  const [ref, { width }] = useMeasure()
  const sizeProps = useSpring({
    width: isSelected ? 215 : width + 55,
    opacity: disabled ? 0.5 : 1,
  })
  const appearanceProps = useSpring({
    enterIconOpacity: isSelected ? 1 : 0,
    closeIconOpacity: !isSelected ? 1 : 0,
    delay: 200
  });

  useEffect(() => {
    if (isSelected) {
      inputRef.current?.focus();
    } else {
      onBlur({ ...option, label: currentValue });
    }
  }, [isSelected])

  useEffect(() => {
    if (option.label.trim().length === 0 && !disabled) {
      setIsSelected(true);
    }
  }, [option])


  const saveBadgeValue = () => {
    setIsSelected(false);
    if (currentValue.trim().length === 0) {
      onRemoveItem();
    } else {
      onBlur({ ...option, label: currentValue });
    }
  };

  return (
    <Badge
      className={cn(['mr-2 my-1 px-1 max-w-[210px]'], disabled && 'pointer-events-none')}
      variant="secondary"
      style={{
        width: sizeProps.width,
        opacity: sizeProps.opacity,
      }}
    >
        {isHovered && !disabled && option.label.trim().length > 0 && (
          <Icon
            type="drag-handler"
            className={`cursor-move absolute left-[-16px]`}
          />
        )}
        {
          isSelected && !disabled
          ? (
            <Input
                ref={inputRef}
                variant="tag"
                size="tag"
                value={currentValue}
                onChange={evt => {
                  setCurrentValue(evt.target.value)
                }}
                onBlur={() => {
                  saveBadgeValue();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveBadgeValue();
                  }
                }}
              />
          ) : (
            <CustomTooltip content={option.label}>
              <div role="button" onClick={() => !disabled && setIsSelected(true)} className="truncate font-light leading-5 text-sm py-1 px-2">
                <span ref={ref}>{option.label}</span>
              </div>
            </CustomTooltip>
          )
        }
        <>
          {!isSelected && !disabled &&
            <CustomTooltip content="Remove answer">
              <animated.div
                role="button"
                style={{opacity: appearanceProps.closeIconOpacity}}
                onClickCapture={onRemoveItem}
              >
                <Icon
                  type="close"
                  className={cn(
                    "ml-1 pr-2 cursor-pointer",
                  )}
                />
              </animated.div>
            </CustomTooltip>
          }
          {
            isSelected &&
            !disabled &&
            <CustomTooltip content="Save answer">
              <animated.div
                role="button"
                style={{opacity: appearanceProps.enterIconOpacity}}
                onClickCapture={() => {
                  setIsSelected(false);
                  if (currentValue.trim().length === 0) {
                    onRemoveItem();
                  } else {
                    onBlur({ ...option, label: currentValue });
                  }
                }}
              >
                <Icon
                  type="submit"
                  className={cn(
                    "ml-1 pr-2 cursor-pointer",
                  )}
                />
              </animated.div>
            </CustomTooltip>
          }
        </>
    </Badge>
  )
};

const reorder = (list: Option[], startIndex: number, endIndex: number) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) {
    return null;
  }
  return <Droppable {...props}>{children}</Droppable>;
};

export interface ImageData {
  base64: string
  mimeType: string
  id: string
  signedUrl: string
}

export interface ValueRendererProps {
  value: Option[]
  onValueChange: (options: Option[]) => void
  children?: React.ReactNode
  withImageSelection?: boolean
  disabled?: boolean
  isPreview?: boolean
}
const DefaultValueRenderer = ({ value, onValueChange, children, withImageSelection, disabled, isPreview }: ValueRendererProps) => {
  const [imagesByOption, setImagesByOption] = useState<Record<string, ImageData>>({});
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [activeImageEdit, setActiveImageEdit] = useState<{
    imageData: ImageData,
    optionValue: string,
    optionUploadId: string,
    optionCropMeta?: {width: number, height: number, x: number, y: number, unit: string}
  } | null>(null);
  const { uploadImage: uploadS3Image, fetchImagesUrlById } = useUploadImage();

  useEffect(() => {
    fetchImages().catch(e => console.error('Fetch images failed', e));
  }, [])

  const fetchImages = async () => {
    // Only process options with valid userUploadId
    const optionsWithImages = value.filter(option => option.userUploadId && option.userUploadId.trim().length > 0);
    const ids = optionsWithImages.map(option => option.userUploadId).filter((id): id is string => id !== undefined);
    const optionValues = optionsWithImages.map(option => option.value);

    // Only set loading state for options that actually have userUploadId
    for (const optionValue of optionValues) {
      setIsUploading(prev => ({ ...prev, [optionValue]: true }));
    }

    if (ids.length === 0) {
      return; // No images to fetch
    }

    try {
      const images = await fetchImagesUrlById(ids, 'original');
      Object.entries(value).forEach(([id, {value: optionValue, userUploadId}]) => {
        if (userUploadId && images[userUploadId]) {
          setImagesByOption(prev => ({
            ...prev,
            [optionValue]: { id, signedUrl: images[userUploadId] } as ImageData
          }));
        }
      });
    } catch (e) {
      console.error('Fetch images failed', e);
    } finally {
      // Always clear loading state for all options that were marked as loading
      for (const optionValue of optionValues) {
        setIsUploading(prev => ({ ...prev, [optionValue]: false }));
      }
    }
  }

  const uploadImage = async ({base64, mimeType, optionValue, cropMeta}: {base64: string, mimeType: string, optionValue: string, cropMeta?: {width: number, height: number, x: number, y: number, unit: string}}) => {
    console.log('uploadImage', base64, mimeType, optionValue, cropMeta);
    setIsUploading(prev => ({ ...prev, [optionValue]: true }));
    try {
      const image = await uploadS3Image({ base64, mimeType, sourceId: '123', sourceType: 'scenario-mock', cropMeta });
      setImagesByOption(prev => ({
        ...prev,
        [optionValue]: {
          base64,
          id: image.id,
          mimeType: image.mimeType,
          signedUrl: image.signedUrl,
        }
      }));
      onValueChange(value.map(opt => opt.value === optionValue ? { ...opt, userUploadId: image.id, cropMeta } : opt));
    } catch (e) {
      console.error('Upload image failed', e);
    } finally {
      setIsUploading(prev => ({ ...prev, [optionValue]: false }));
      fetchImages().catch(e => console.error('Fetch images failed', e));
    }

  }

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      value,
      result.source.index,
      result.destination.index
    );

    onValueChange(items);
  }

  const handleImageUpload = (optionValue: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      uploadImage({base64, mimeType: file.type, optionValue}).catch(e => console.error('Upload image failed', e));
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, optionValue: string) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(optionValue, file);
    }
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const triggerFileInput = (optionValue: string) => {
    fileInputRefs.current[optionValue]?.click();
  };

  const setFileInputRef = (optionValue: string) => (ref: HTMLInputElement | null) => {
    fileInputRefs.current[optionValue] = ref;
  };

  const replaceImage = ({ optionValue }: { optionValue: string }) => {
    setActiveImageEdit(null);
    setImagesByOption(prev => {
      const newImages = { ...prev };
      delete newImages[optionValue];
      return newImages;
    });
    onValueChange(value.map(opt => opt.value === optionValue ? { ...opt, userUploadId: undefined } : opt));
    setTimeout(() => {
      triggerFileInput(optionValue);
    }, 100);
  }

  const ImagePlaceholder = ({ optionValue }: { optionValue: string }) => {
    if (isUploading[optionValue]) {
      return (
        <div
          className="flex items-center justify-center w-[34px] h-[34px] mr-2 animate-pulse bg-gray-200 rounded-lg cursor-pointer hover:bg-gray-300"
          onClick={() => triggerFileInput(optionValue)}
        >
          <Icon type="image-placeholder" color="black" className="w-8 h-8 text-gray-400" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center w-[34px] h-[34px] rounded bg-gray-50 hover:bg-gray-100 cursor-pointer mr-2">
        <div
          onClick={() => triggerFileInput(optionValue)}
        >
          <Icon type="image-placeholder" color="gray" className="w-[34px] h-[34px] text-gray-400 rounded-lg" />
        </div>
      </div>
    );
  };

  console.log('value', value);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable
        droppableId="droppable"
      >
        {(provided) => (
          <div
            className="w-full flex flex-wrap flex-col items-start drop-wrapper"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {value.map((option, idx) => (
              <Draggable key={option.value} draggableId={option.value} index={idx} isDragDisabled={disabled ?? isPreview}>
                {(provided, snapshot) => {
                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  const [isHovered, setIsHovered] = useState(false)
                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  const [currentItemTop, setCurrentItemTop] = useState(0)
                  const topInfoMenuCoord = document.querySelector('.info-menu')?.getBoundingClientRect().top ?? 0;
                  const currentItemTopRelative = document.querySelector(`[data-rbd-drag-handle-draggable-id="${option.value}"]`)?.getBoundingClientRect().top ?? 0;

                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  useEffect(() => {
                    if (!snapshot.isDragging) {
                      setCurrentItemTop(currentItemTopRelative - topInfoMenuCoord)
                    }
                  }, [currentItemTopRelative, topInfoMenuCoord]);

                  console.log('option', option);

                  return (
                    <div
                      key={option.value}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={
                        {
                          ...provided.draggableProps.style,
                          left: undefined,
                          top: currentItemTop,
                        }
                      }
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      className="flex items-center"
                    >
                      {withImageSelection && (
                        <input
                          ref={setFileInputRef(option.value)}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileInputChange(e, option.value)}
                        />
                      )}
                      {withImageSelection && option.label.trim().length > 0 && (
                        imagesByOption[option.value]?.signedUrl ? (
                          <div className="relative w-[34px] h-[34px] mr-2">
                            <img
                              src={imagesByOption[option.value]?.signedUrl}
                              alt="Selected"
                              className="w-full h-full object-cover rounded-md border cursor-pointer"
                              onClick={() => setActiveImageEdit({
                                imageData: imagesByOption[option.value],
                                optionValue: option.value,
                                optionUploadId: option.userUploadId ?? '',
                                optionCropMeta: option.cropMeta
                              })}
                          />
                          </div>
                        ) : (
                          <ImagePlaceholder optionValue={option.value} />
                        )
                      )}
                      <InteractiveBadge
                        key={option.value}
                        isHovered={snapshot.isDragging || isHovered}
                        option={option}
                        onBlur={(updatedItem: Option) => {
                          console.log('updatedItem', updatedItem)
                          const updatedValue = value.map((opt) => opt.value === option.value ? updatedItem : opt);
                          onValueChange?.(updatedValue)
                        }}
                        withImageSelection={withImageSelection}
                        onRemoveItem={() => onValueChange?.(value.filter((opt) => opt.value !== option.value))}
                        disabled={disabled ?? isPreview}
                      />
                    </div>
                  )}
                }
              </Draggable>
            ))}
            {provided.placeholder}
            {children}
            {activeImageEdit && (
              <ImageEditModal
                key={activeImageEdit.optionValue}
                isOpen={!!activeImageEdit}
                onOpenChange={(isOpen) => setActiveImageEdit(isOpen ? activeImageEdit : null)}
                optionValue={activeImageEdit.optionValue}
                optionUploadId={activeImageEdit.optionUploadId}
                optionCropMeta={activeImageEdit.optionCropMeta}
                onReplace={(optionValue) => replaceImage({ optionValue })}
                onSave={(croppedImageData, optionValue, cropMeta) => {
                  const mimeType = croppedImageData.split(';')[0].split(':')[1];
                  uploadImage({base64: croppedImageData, mimeType, optionValue, cropMeta}).catch(e =>
                    console.error('Upload cropped image failed', e)
                  );
                }}
              />
            )}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
)}

export interface MultiOptionsSelectorProps {
  value?: Option[]
  options?: Option[]
  ignoredValues?: string[]
  disabled?: boolean
  isValid?: boolean
  isPreview?: boolean
  onValueChange?: (value: Option[], extraOpts?: Record<string, unknown>) => void,
  withImageSelection?: boolean
  Renderer?: (props: ValueRendererProps) => React.ReactNode
}
export function MultiOptionsSelector({
    value = [],
    disabled,
    isPreview,
    isValid = true,
    onValueChange,
    Renderer=DefaultValueRenderer,
    withImageSelection
  }: MultiOptionsSelectorProps) {
  const searchRef = React.useRef<HTMLInputElement>(null);

  const handleAddOption = () => {
    const optionToSelect: Option = { label: '', value: uuid(), userUploadId: undefined };

    onValueChange?.([...value, optionToSelect])
    searchRef.current?.focus();
  };

  console.log('value', value);

  return (
    <div>
      <Renderer value={value} onValueChange={onValueChange!} withImageSelection={withImageSelection} disabled={disabled} isPreview={isPreview}>
        {!isPreview && (
          <CustomTooltip content="Add option">
            <Button
              variant="ghost"
              size="sm"
              role="combobox"
              disabled={
                disabled ??
                isPreview ??
                value.some(option => option.label.trim().length === 0)
              }
              className={cn(["justify-between pl-2 pr-4", { 'border-red-500': !isValid, 'border-[1px]': !isValid }])}
              onClickCapture={handleAddOption}
            >
              <Icon type="plus" />
              Add
            </Button>
          </CustomTooltip>
        )}
      </Renderer>
    </div>
  )
}
